import { Box } from "@outschool/backpack";
import { LearnerAttributes } from "@outschool/gql-frontend-generated";
import { Trans, useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import {
  addParamsToUrl,
  browseRootPath,
  loginPath,
  searchPath,
  teachPath,
} from "@outschool/routes";
import { useAnalytics } from "@outschool/ui-analytics";
import {
  ContinueWithApple,
  ContinueWithGoogle,
  ContinueWithKakao,
  ContinueWithLine,
  EmailSignupWithinModalForm,
  LoginUserFeedback,
  PostLoginAction,
  getNewUrlAfterLogin,
  getPostLoginAction,
  getPostLoginPath,
  useAuthError,
  useCanShowGoogleAuth,
  useEsaExperience,
  useShouldShowKakao,
  useShouldShowLine,
} from "@outschool/ui-auth";
import { AccountDisclaimer } from "@outschool/ui-components-shared";
import { TeacherRegistrationLink } from "@outschool/ui-components-website";
import { useNavigation } from "@outschool/ui-utils";
import React, { useState } from "react";

import {
  APPLE_SIGNIN_CLIENT_ID,
  FF_USE_TURNSTILE_SIGNUP,
  GOOGLE_CLIENT_ID,
  TURNSTILE_SITEKEY,
} from "../../shared/Env";
import ActionType from "../actions/ActionType";
import {
  useLoginWithApple,
  useLoginWithRedirect,
} from "../hooks/Authentication";
import { useAppState } from "../stores/AppStateProvider";
import Link from "./Link";
import { useMembershipRequirement } from "./subscriptions/hooks/useMembershipRequirement";

interface SignupFormProps {
  onRedirect?: (e: React.MouseEvent<Element, MouseEvent>) => void;
  onClickSocialSignup?: () => void;
  promptLogin?: boolean;
  isTeacherFlow?: boolean;
  trackingParameters?: { [key: string]: string };
  trackingUniqueId?: string;
  signupParams?: {
    isGiftCardSignup?: boolean;
    defaultLearnerAttributes?: LearnerAttributes;
    postLoginPath?: string;
    redirect?: string;
  };
  loginParams?: {
    [key: string]: string | boolean | number;
  };
  joinFromNavBar?: boolean;
  setIsRedirecting?: (isRedirecting: boolean) => void;
}

export default function SignupForm({
  onRedirect,
  onClickSocialSignup,
  promptLogin,
  isTeacherFlow,
  trackingParameters,
  trackingUniqueId,
  signupParams,
  loginParams,
  joinFromNavBar,
  setIsRedirecting = () => {},
}: SignupFormProps) {
  const newUrlAfterLogin = getNewUrlAfterLogin();
  const postLoginPath = getPostLoginPath();
  const postLoginAction = getPostLoginAction();
  const { t } = useTranslation("client\\components\\SignupForm");
  const [disableSignupButtons, setDisableSignupButtons] = useState(false);

  // Conditionally show email signup form within modal
  const [showEmailSignupForm, setShowEmailSignupForm] = useState(false);

  const redirectPathAfterLogin = loginParams?.redirect
    ? String(loginParams?.redirect)
    : newUrlAfterLogin ?? postLoginPath;

  return (
    <Box
      sx={{
        display: "grid",
        gap: "1em",
        width: "100%",
      }}
    >
      <SignupButtons
        isTeacherFlow={isTeacherFlow}
        trackingParameters={trackingParameters}
        trackingUniqueId={trackingUniqueId}
        signupParams={signupParams}
        postLoginPath={postLoginPath}
        postLoginAction={postLoginAction}
        onClickSocialSignup={onClickSocialSignup}
        setDisableSignupButtons={setDisableSignupButtons}
        disableSignupButtons={disableSignupButtons}
        setShowEmailSignupForm={setShowEmailSignupForm}
        showEmailSignupForm={showEmailSignupForm}
        setIsRedirecting={setIsRedirecting}
      />
      {promptLogin && joinFromNavBar ? (
        <TeacherRegistrationLink
          onRedirect={onRedirect}
          loginParams={loginParams}
          postLoginPath={redirectPathAfterLogin}
          trackingUniqueId={trackingUniqueId}
        />
      ) : promptLogin ? (
        <Trans t={t}>
          <Box
            sx={{
              justifySelf: "center",
            }}
          >
            Already have an account?{" "}
            <Link
              data-test-id="login-link"
              onClick={onRedirect}
              to={addParamsToUrl(loginPath(), {
                postLoginPath: redirectPathAfterLogin,
                ...loginParams,
              })}
              trackingName="login"
              trackingUniqueId={trackingUniqueId}
            >
              Log in
            </Link>
          </Box>
        </Trans>
      ) : null}
      <AccountDisclaimer
        isSignup={true}
        showEmailSignupWithinModal={showEmailSignupForm}
      />
    </Box>
  );
}

interface SignupButtonProps {
  isTeacherFlow?: boolean;
  trackingParameters?: { [key: string]: string };
  trackingUniqueId?: string;
  signupParams?: {
    isGiftCardSignup?: boolean;
    defaultLearnerAttributes?: LearnerAttributes;
    postLoginPath?: string;
    redirect?: string;
  };
  postLoginPath?: string | null;
  postLoginAction?: PostLoginAction;
  onClickSocialSignup?: () => void;
  setDisableSignupButtons: React.Dispatch<React.SetStateAction<boolean>>;
  disableSignupButtons: boolean;
  setShowEmailSignupForm: React.Dispatch<React.SetStateAction<boolean>>;
  showEmailSignupForm: boolean;
  setIsRedirecting?: (isRedirecting: boolean) => void;
}

function SignupButtons({
  isTeacherFlow,
  trackingParameters,
  trackingUniqueId,
  signupParams,
  postLoginPath,
  postLoginAction,
  onClickSocialSignup,
  setDisableSignupButtons,
  disableSignupButtons,
  setShowEmailSignupForm,
  showEmailSignupForm,
  setIsRedirecting,
}: SignupButtonProps) {
  const analytics = useAnalytics();
  const { t } = useTranslation("client\\components\\SignupForm");
  const [userFeedbackMessage, setUserFeedbackMessage] =
    React.useState<React.ReactNode>(null);
  const appState = useAppState();
  const canShowGoogleAuth = useCanShowGoogleAuth();
  const navigate = useNavigation();
  const { requiresMembershipForEnrollment } = useMembershipRequirement();
  const { createAccountVariables } = useEsaExperience();

  const postLoginPathIsRoot =
    !postLoginPath || postLoginPath === browseRootPath();
  const signupToSearchOrPostLoginPath = Boolean(signupParams?.redirect)
    ? signupParams?.redirect
    : postLoginPathIsRoot
    ? searchPath()
    : postLoginPath;

  const { isGiftCardSignup, defaultLearnerAttributes } = signupParams || {};

  const createParams = {
    isGiftCardSignup,
    isLeader: isTeacherFlow,
    trackingParameters,
    defaultLearnerAttributes,
    requiresMembershipForEnrollment,
    selfElectedStateFundingProgram: createAccountVariables(),
  };

  const defaultErrorMessage = t("Sorry that didn’t work, try again");
  const handleAuthError = React.useCallback(
    (error: $TSFixMe, message: React.ReactNode = defaultErrorMessage) => {
      OsPlatform.captureError(error, {
        tags: { component: Component.UserAccountManagement },
      });
      setUserFeedbackMessage(message);
    },
    [defaultErrorMessage, setUserFeedbackMessage],
  );

  const generateLoginError = useAuthError();

  const handleLoginSuccess = React.useCallback(
    ({
      sessionToken,
      refreshToken,
      postLoginPath,
      isLearnerTransfer,
    }: $TSFixMe) => {
      if (!isLearnerTransfer) {
        appState.appDispatcher.dispatch(ActionType.User.LOGGED_IN, {
          sessionToken,
          refreshToken,
        });
        const redirectPath =
          signupParams?.redirect ||
          (isTeacherFlow ? teachPath() : postLoginPath);
        appState.redirectAfterLogin(redirectPath);
      } else {
        appState.appDispatcher.dispatch(
          ActionType.User.REDIRECT_TO_LEARNER_APP,
          {
            transferToken: sessionToken,
            isLearnerLogin: true,
          },
        );
      }
    },
    [isTeacherFlow, signupParams?.redirect, appState],
  );

  const handleLoginError = React.useCallback(
    (error: $TSFixMe) => {
      const errorObject = new Error(error);
      handleAuthError(errorObject, generateLoginError(error));
    },
    [handleAuthError, generateLoginError],
  );

  const handleAppleLogin = useLoginWithApple(
    handleLoginSuccess,
    handleLoginError,
    createParams,
    signupToSearchOrPostLoginPath,
  );

  const setAuthStrategy = useLoginWithRedirect(
    handleLoginError,
    createParams,
    postLoginAction,
    signupToSearchOrPostLoginPath,
  );

  const showKakao = useShouldShowKakao();
  const showLine = useShouldShowLine();

  return (
    <React.Fragment>
      <LoginUserFeedback
        userFeedbackMessage={userFeedbackMessage}
        isError={true}
      />
      {canShowGoogleAuth && (
        <ContinueWithGoogle
          trackingName="signup-with-google"
          setAuthStrategy={setAuthStrategy}
          // @ts-ignore TS(2322): Type '() => Promise<string | undefined>' is not as... Remove this comment to see the full error message
          getAnonymousId={analytics.anonymousId}
          clientId={GOOGLE_CLIENT_ID!}
          onClick={() => {
            onClickSocialSignup && onClickSocialSignup();
          }}
          disableWhileLoading={disableSignupButtons}
        />
      )}

      {showLine && (
        <ContinueWithLine
          isLearnerApp={false}
          setAuthStrategy={setAuthStrategy}
          trackingName="signup-with-line"
          getAnonymousId={analytics.anonymousId}
          disableWhileLoading={disableSignupButtons}
        />
      )}

      {showKakao && (
        <ContinueWithKakao
          isLearnerApp={false}
          setAuthStrategy={setAuthStrategy}
          trackingName="signup-with-kakao"
          getAnonymousId={analytics.anonymousId}
          disableWhileLoading={disableSignupButtons}
        />
      )}

      <ContinueWithApple
        onSuccess={handleAppleLogin}
        onError={handleAuthError}
        trackingName="signup-with-apple"
        clientId={APPLE_SIGNIN_CLIENT_ID!}
        onClick={() => {
          onClickSocialSignup && onClickSocialSignup();
        }}
        disableWhileLoading={disableSignupButtons}
      />
      <EmailSignupWithinModalForm
        showEmailSignupForm={showEmailSignupForm}
        setShowEmailSignupForm={setShowEmailSignupForm}
        setDisableSignupButtons={setDisableSignupButtons}
        signupParams={signupParams}
        trackingUniqueId={trackingUniqueId}
        onUserLoggedIn={(sessionToken: string, refreshToken: string) => {
          appState.appDispatcher.dispatch(ActionType.User.LOGGED_IN, {
            sessionToken,
            refreshToken,
          });
          const redirectPath = signupToSearchOrPostLoginPath;
          if (redirectPath) {
            navigate(redirectPath);
          } else {
            window.location.reload();
          }
        }}
        onUserRefresh={() => {
          appState.appDispatcher.dispatch(ActionType.User.REFRESH);
        }}
        isTeacherFlow={isTeacherFlow}
        // setIsRedirecting is always set when the modal renders SignupForm
        setIsRedirecting={setIsRedirecting as (isRedirecting: boolean) => void}
        requiresMembershipForEnrollment={requiresMembershipForEnrollment}
        turnstileSitekey={
          FF_USE_TURNSTILE_SIGNUP ? TURNSTILE_SITEKEY : undefined
        }
      />
    </React.Fragment>
  );
}
